<template>
  <div>
    <div class="page-header">
      <div class="cover">
        <h1 class="has-text-white is-family-secondary">
          Student Leaders <br />
          Training
        </h1>
      </div>
    </div>
    <div class="container section tw-mt-5">
      <div class="columns">
        <div class="column">
          <div class="section-block">
            <span class="title">STUDENT LEADER RESOURCES</span>
          </div>
          <div class="is-narrow" style="margin-top: -15px;">
            <b-field grouped position="is-right">
              <b-radio-button
                v-model="bookmarked"
                native-value="1"
                type="is-danger"
                class="bookmark-button"
                ><b-icon icon="bookmark" type="is-danger"></b-icon
              ></b-radio-button>
              <b-radio-button
                v-model="bookmarked"
                native-value="0"
                type="is-light"
                class="bookmark-button"
                ><b-icon icon="bookmark" type="is-light"></b-icon
              ></b-radio-button>
              <div class="tw-mt-1" style="width: 32px;">
                <a @click.stop.prevent="resetFilter()" v-if="isBookmarked">
                  <b-icon icon="close" type="is-light"></b-icon>
                </a>
              </div>
            </b-field>
          </div>

          <div v-for="category in resourceCategories.data" :key="category.id">
            <div v-if="category.resources.data.length > 0" class="tw-mb-20">
              <div class="tw-my-5 tw-justify-between clear-both">
                <div
                  class="content quill font-weight-thin"
                  v-html="category.display_name"
                />
              </div>
              <skeleton-loading class="column is-6 tw-p-0" v-if="loading" />
              <div class="columns is-multiline" v-else>
                <div
                  v-for="resource in filterResources(category.resources.data)"
                  :key="resource.id"
                  class="column is-3"
                >
                  <resource-card
                    redirect="training.resources.view"
                    :resource="newResource(resource)"
                    :loading.sync="loading"
                  />
                </div>
              </div>
              <b-pagination
                class="tw-mt-8"
                @click.stop=""
                v-if="category.resources.meta.last_page > 1"
                @change="setCategoryResources(category.id, $event)"
                :total="category.resources.meta.total"
                :current.sync="category.resources.meta.current_page"
                :per-page="category.resources.meta.per_page"
                order="is-centered"
                aria-next-label="Next page"
                aria-previous-label="Previous page"
              >
              </b-pagination>
            </div>
          </div>

          <div class="section-block tw-mt-8">
            <span class="title tw-uppercase">
              Leadership Blog
            </span>
          </div>
          <promised
            :promise="blogsPromise"
            v-slot:combined="{ isPending, data }"
          >
            <skeleton-loading v-if="isPending" class="column is-4 tw-p-0" />
            <template v-else-if="data">
              <div class="columns is-multiline">
                <div
                  v-for="blog in data.data"
                  :key="blog.id"
                  class="column is-4"
                >
                  <blog-card :blog="blog" redirect-to="training.blogs.view" />
                </div>
              </div>
              <b-pagination
                class="tw-mt-8"
                v-if="data.meta.last_page > 1"
                @change="getTrainingBlogs"
                :total="data.meta.total"
                :current.sync="data.meta.current_page"
                :per-page="data.meta.per_page"
                order="is-centered"
                aria-next-label="Next page"
                aria-previous-label="Previous page"
              >
              </b-pagination>
            </template>
          </promised>
        </div>
        <div class="column is-one-third">
          <div class="section-block">
            <span class="title">SUGGEST A RESOURCE</span>
          </div>
          <suggestion-form />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import BlogCard from '@/components/BlogCard'
import Blog from '@/models/Blog'
import Resource from '@/models/Resource'
import ResourceCard from '@/components/ResourceCard'
import SuggestionForm from '@/components/SuggestionForm'
import ResourceCategory from '@/models/ResourceCategory'
import * as BlogType from '@/utils/consts/BlogType'
import * as ResourceType from '@/utils/consts/ResourceType'
import forEach from 'lodash/forEach'

export default {
  metaInfo: {
    title: 'Student Leaders Training'
  },
  components: {
    BlogCard,
    ResourceCard,
    SuggestionForm
  },
  data() {
    return {
      resourcesPromise: null,
      blogsPromise: null,
      bookmarked: '',
      isBookmarked: null,
      loading: false,
      resourceCategories: {
        data: [],
        meta: {
          current_page: 1
        }
      },
      resourcePage: {
        id: null
      }
    }
  },
  mounted() {
    this.getAllResources()
  },
  watch: {
    bookmarked(n) {
      if (n !== null) {
        this.isBookmarked = this.bookmarked
      } else {
        this.isBookmarked = null
      }
      this.getAllResources()
    }
  },
  methods: {
    newResource(resouce) {
      return new Resource(resouce)
    },
    async getResourcePromise(catId, page = 1) {
      this.loading = true
      return Resource.include('cover')
        .where('type', ResourceType.TRAINING)
        .where('resource_category_id', catId)
        .where('is_bookmarked', this.isBookmarked)
        .page(parseInt(page))
        .limit(8)
        .get()
    },
    async getCategories(page = 1) {
      this.loading = true
      return ResourceCategory.where('type', ResourceType.TRAINING)
        .where('is_bookmarked', this.isBookmarked)
        .page(1)
        .limit(9999)
        .get()
        .then(async response => {
          this.resourceCategories = response
          await this.setCategoryResources(null, page)
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false
        })
    },
    async setCategoryResources(catId, page) {
      await forEach(this.resourceCategories, async categories => {
        if (Array.isArray(categories)) {
          await forEach(categories, async category => {
            if (category.resources.data.length > 0) {
              this.$set(category.resources, 'data', [])

              if (catId === category.id) {
                this.resourcePage.id = catId
                this.$set(
                  category,
                  'resources',
                  await this.getResourcePromise(catId, page)
                )
              } else {
                this.$set(
                  category,
                  'resources',
                  await this.getResourcePromise(category.id)
                )
              }
            }
          })
        }
      })
      this.loading = false
    },

    getTrainingBlogs(page = 1) {
      this.blogsPromise = Blog.include('created_by', 'cover')
        .where('type', BlogType.TRAINING)
        .page(parseInt(page))
        .limit(8)
        .get()
    },

    getAllResources() {
      this.getCategories()
      this.getTrainingBlogs()
    },

    resetFilter() {
      this.bookmarked = null
      this.getAllResources()
    },

    filterResources(resources) {
      return resources.filter(
        resource =>
          this.isBookmarked === null ||
          (parseInt(this.isBookmarked) === 1 && resource.is_bookmarked) ||
          (parseInt(this.isBookmarked) === 0 && resource.is_bookmarked === null)
      )
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  background: center / cover no-repeat url(/img/training-header.jpg) !important;
}

.quill {
  font-size: 1.5rem !important;
  p {
    strong {
      font-weight: bolder !important;
    }
  }
}
</style>
